import React, { useState } from "react";
import { useIntl } from "react-intl";
import {
  Box,
  Button,
  Chip,
  Dialog,
  DialogContent,
  DialogTitle,
  MenuItem,
  TextField,
  Typography,
} from "@mui/material";
import { booksApi, BookHit } from "../../../../features/books/booksApi";
import { useAppSelector } from "../../../../app/hooks";
import { ui, tone } from "../../../../utils/uiTokens";

const ROLES = ["MODERATOR", "SUPER_ADMIN", "SUBJECT_HEAD", "CURATOR"];

const strip = (s: string | null) => (s || "").replace(/<[^>]+>/g, "");

const HitRow = ({ hit, action }: { hit: BookHit; action: React.ReactNode }) => {
  const { formatMessage } = useIntl();
  return (
    <Box
      sx={{
        display: "flex",
        gap: 2,
        alignItems: "flex-start",
        justifyContent: "space-between",
        flexWrap: "wrap",
        p: 1.5,
        mb: 1,
        border: `1px solid ${ui.border}`,
        borderRadius: ui.radius,
        backgroundColor: ui.surface,
      }}
    >
      <Box sx={{ flex: "1 1 240px", minWidth: 0 }}>
        <Typography sx={{ color: ui.ink, fontWeight: 600, fontSize: 14 }}>
          {hit.bookTitle}
        </Typography>
        <Box sx={{ display: "flex", gap: 0.5, my: 0.5, flexWrap: "wrap" }}>
          {hit.grade ? (
            <Chip
              size="small"
              label={formatMessage({ id: "bookPagesGrade" }, { n: hit.grade })}
              sx={{
                color: tone.neutral.text,
                backgroundColor: tone.neutral.bg,
              }}
            />
          ) : null}
          <Chip
            size="small"
            label={formatMessage(
              { id: "bookPagesPage" },
              { n: hit.pageNumber }
            )}
            sx={{ color: tone.info.text, backgroundColor: tone.info.bg }}
          />
        </Box>
        <Typography
          sx={{ color: ui.sub, fontSize: 13, wordBreak: "break-word" }}
        >
          {strip(hit.snippet)}
        </Typography>
      </Box>
      {action}
    </Box>
  );
};

const AddDialog = ({
  open,
  onClose,
  courseTopicId,
  topicTitle,
  defaultLang,
  defaultSubject,
}: {
  open: boolean;
  onClose: () => void;
  courseTopicId: number;
  topicTitle: string;
  defaultLang: string;
  defaultSubject: string;
}) => {
  const { formatMessage } = useIntl();
  const [q, setQ] = useState(topicTitle);
  const [subject, setSubject] = useState(defaultSubject);
  const [lang, setLang] = useState(defaultLang);
  const [applied, setApplied] = useState({
    q: topicTitle,
    subject: defaultSubject,
    lang: defaultLang,
  });
  const { data: subjects = [] } = booksApi.useGetBookSubjectsQuery(undefined, {
    skip: !open,
  });
  const { data, isFetching } = booksApi.useSearchBooksQuery(applied, {
    skip: !open || !applied.q.trim(),
  });
  const [addRef, { isLoading: adding }] = booksApi.useAddBookRefMutation();

  const search = () => setApplied({ q: q.trim(), subject, lang });

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="md">
      <DialogTitle sx={{ color: ui.ink }}>
        {formatMessage({ id: "bookPagesAdd" })}
      </DialogTitle>
      <DialogContent>
        <Box sx={{ display: "flex", gap: 1.5, flexWrap: "wrap", mt: 1, mb: 2 }}>
          <TextField
            size="small"
            sx={{ flex: "2 1 240px" }}
            value={q}
            label={formatMessage({ id: "bookPagesSearch" })}
            onChange={(e) => setQ(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && search()}
          />
          <TextField
            select
            size="small"
            sx={{ flex: "1 1 160px" }}
            value={subject}
            label={formatMessage({ id: "bookPagesSubject" })}
            onChange={(e) => setSubject(e.target.value)}
          >
            <MenuItem value="">
              {formatMessage({ id: "bookPagesAnySubject" })}
            </MenuItem>
            {subjects.map((s) => (
              <MenuItem key={s} value={s}>
                {s}
              </MenuItem>
            ))}
          </TextField>
          <TextField
            select
            size="small"
            sx={{ flex: "0 1 100px" }}
            value={lang}
            label={formatMessage({ id: "bookPagesLang" })}
            onChange={(e) => setLang(e.target.value)}
          >
            <MenuItem value="kz">KZ</MenuItem>
            <MenuItem value="ru">RU</MenuItem>
          </TextField>
          <Button
            variant="contained"
            onClick={search}
            disabled={!q.trim() || isFetching}
          >
            {formatMessage({ id: "bookPagesFind" })}
          </Button>
        </Box>
        {data && data.items.length === 0 && !isFetching && (
          <Typography sx={{ color: ui.muted }}>
            {formatMessage({ id: "bookPagesNothing" })}
          </Typography>
        )}
        {(data?.items || []).map((hit) => (
          <HitRow
            key={`${hit.bookId}-${hit.pageNumber}`}
            hit={hit}
            action={
              <Button
                variant="outlined"
                size="small"
                disabled={adding}
                onClick={() => addRef({ courseTopicId, hit })}
              >
                {formatMessage({ id: "bookPagesPin" })}
              </Button>
            }
          />
        ))}
      </DialogContent>
    </Dialog>
  );
};

const TopicBookPages = ({
  courseTopicId,
  topicTitle,
  lang,
}: {
  courseTopicId: number;
  topicTitle: string;
  lang: string;
}) => {
  const { formatMessage } = useIntl();
  const role = useAppSelector((state) => state.user.userInfo.roles[0]);
  const allowed = ROLES.includes(role);
  const [open, setOpen] = useState(false);
  const { data } = booksApi.useGetTopicBooksQuery(courseTopicId, {
    skip: !allowed,
  });
  const [removeRef, { isLoading: removing }] =
    booksApi.useRemoveBookRefMutation();

  if (!allowed) return null;
  const pinned = data?.pinned ? data.items : [];

  return (
    <Box sx={{ mt: 4, width: "100%", maxWidth: 720 }}>
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          mb: 1.5,
        }}
      >
        <Typography sx={{ color: ui.ink, fontWeight: 600, fontSize: 18 }}>
          {formatMessage({ id: "bookPages" })}
        </Typography>
        <Button variant="outlined" onClick={() => setOpen(true)}>
          {formatMessage({ id: "bookPagesAdd" })}
        </Button>
      </Box>
      {pinned.length === 0 && (
        <Typography sx={{ color: ui.muted, fontSize: 14 }}>
          {formatMessage({ id: "bookPagesEmpty" })}
        </Typography>
      )}
      {pinned.map((hit) => (
        <HitRow
          key={hit.refId ?? `${hit.bookId}-${hit.pageNumber}`}
          hit={hit}
          action={
            <Button
              size="small"
              sx={{ color: tone.bad.text }}
              disabled={removing || !hit.refId}
              onClick={() =>
                removeRef({ courseTopicId, refId: hit.refId as number })
              }
            >
              {formatMessage({ id: "bookPagesUnpin" })}
            </Button>
          }
        />
      ))}
      {open && (
        <AddDialog
          open={open}
          onClose={() => setOpen(false)}
          courseTopicId={courseTopicId}
          topicTitle={data?.topicTitle || topicTitle}
          defaultLang={data?.lang || lang}
          defaultSubject={data?.subject || ""}
        />
      )}
    </Box>
  );
};

export default TopicBookPages;
